import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import { Tool, ToolSlot } from 'cd-eft/components/Design/BreaklineTools/framework/Tool';
import { esriGeometryEngine, esriProjection, Polygon, Polyline } from 'cd-eft/utils/esriTypes';
import { computeAcres, explodeMultipartPolygon } from 'cd-eft/utils/gisUtils';
import { DrawPolylineInputTask } from './DrawPolylineInputTask';

export class SplitLandUsePolygonsTool extends Tool {
  private readonly mapView: MapView;
  private task: DrawPolylineInputTask;
  private landUsePolygonsLayer: GraphicsLayer;
  private errorReset: () => void;
  private mapContainer: HTMLElement;

  constructor(mapView: MapView, toolSlot: ToolSlot, landUsePolygonsLayer: GraphicsLayer, callback: () => void) {
    super('Split Land Use Polygon', 'single', toolSlot);
    this.mapView = mapView;
    this.task = new DrawPolylineInputTask(this.mapView, { allowSelfIntersecting: false });
    this.landUsePolygonsLayer = landUsePolygonsLayer;
    this.errorReset = callback;
    this.mapContainer = this.mapView.container as HTMLElement;

    document.addEventListener('mousedown', this.handleOutsideClick);
  }

  private handleOutsideClick = (event: MouseEvent): void => {
    if (!this.mapContainer.contains(event.target as Node)) {
      this.deactivate();
    }
  };

  public async deactivate(): Promise<void> {
    document.removeEventListener('mousedown', this.handleOutsideClick);
    if (this.task.isExecuting) {
      await this.task.cancelTaskAsync();
    }
  }

  protected async onExecuteOperation(): Promise<void> {
    const result = await this.task.executeTaskAsync();

    let cuttingLine: Polyline | undefined = undefined;

    const splitGraphics: Graphic[] = [];
    const splitShapes: Polygon[][] = [];
    let maxObjectId = -1;
    for (const graphic of this.landUsePolygonsLayer.graphics) {
      if (graphic.attributes.OBJECTID > maxObjectId) {
        maxObjectId = graphic.attributes.OBJECTID;
      }
      const polygon = graphic.geometry as Polygon;
      if (!cuttingLine || cuttingLine.spatialReference.wkid !== polygon.spatialReference.wkid) {
        cuttingLine = esriProjection.project(result, polygon.spatialReference) as Polyline;
      }
      if (esriGeometryEngine.crosses(cuttingLine, polygon)) {
        //console.log('OBJECTID', graphic.attributes.OBJECTID);
        const polygons = esriGeometryEngine.cut(polygon, cuttingLine) as Polygon[];
        //console.log('polygons', polygons);
        if (polygons.length === 0) {
          // console.log('no cut occured');
        } else if (polygons.length === 2 || polygons.length > 2) {
          splitGraphics.push(graphic);
          splitShapes.push(polygons);
        } else {
          // console.log('unexpected result, this polygon will not be cut');
        }
      }
    }

    for (const graphic of splitGraphics) {
      const index = splitGraphics.indexOf(graphic);
      const shapes = splitShapes[index];
      //console.log(shapes);
      let origIndex = this.landUsePolygonsLayer.graphics.indexOf(graphic);
      let origIdTaken = false;
      for (const shape of shapes) {
        if (shape.rings.length > 1) {
          const shapes2 = explodeMultipartPolygon(shape);
          for (const shape2 of shapes2) {
            const newGraphic = new Graphic(); // graphic.clone();
            newGraphic.symbol = graphic.symbol;
            newGraphic.geometry = shape2;
            newGraphic.attributes = {
              Acres: computeAcres(shape2),
              CONDITION: null,
              LANDUSE: null,
              OBJECTID: !origIdTaken ? graphic.attributes.OBJECTID : ++maxObjectId,
            };
            origIdTaken = true;
            this.landUsePolygonsLayer.graphics.add(newGraphic, origIndex++);
          }
        } else {
          const newGraphic = new Graphic(); // graphic.clone();
          newGraphic.symbol = graphic.symbol;
          newGraphic.geometry = shape;
          newGraphic.attributes = {
            Acres: computeAcres(shape),
            CONDITION: null,
            LANDUSE: null,
            OBJECTID: !origIdTaken ? graphic.attributes.OBJECTID : ++maxObjectId,
          };
          origIdTaken = true;
          this.landUsePolygonsLayer.graphics.add(newGraphic, origIndex++);
        }
      }
      this.landUsePolygonsLayer.graphics.remove(graphic);

      //add new graphics to layer
      //remove old graphics from layer
    }

    this.errorReset();
    return;
  }

  protected async onCancelOperation(): Promise<void> {
    if (this.task.isExecuting) {
      await this.task.cancelTaskAsync();
    }
    return;
  }
}
